import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAnalytics } from '@vaptmenu/analytics-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import type { ApiError } from '@/shared/lib/problem-details';
import {
  abandonWhatsappSignupSession,
  completeWhatsappSignupSession,
  createWhatsappSignupSession,
  disconnectWhatsapp,
  getWhatsappStatus,
} from '../api/whatsapp.api';
import { apiErrorStatus, completeFailureReason } from '../lib/whatsapp-errors';
import type {
  CompleteWhatsappSignupRequest,
  WhatsappAbandonReason,
  WhatsappSignupMode,
  WhatsappSignupSession,
  WhatsappStatus,
} from '../types/whatsapp.types';
import { invalidateWhatsappStatus, whatsappKeys } from './whatsapp-keys';

/* ==========================================================================
   Leitura e mutações da conexão do WhatsApp (change whatsapp-channel)

   Os eventos de analytics saem só quando a API confirma o fato (2xx, ou a
   recusa do `complete` com um `errorCode` da tabela), nunca no clique
   (spec `product-analytics`). Nenhum leva o número conectado.
   ========================================================================== */

/**
 * Revalidação de segurança do status. O que importa chega pela campainha
 * `whatsapp.connection_updated` e pela reconexão do tempo real; a leitura
 * periódica só cobre um evento perdido e a passagem do tempo do lembrete dos
 * 14 dias.
 */
export const WHATSAPP_STATUS_POLL_MS = 5 * 60_000;

function useTenantId(): string {
  return useAuthStore((state) => state.selectedTenant?.id ?? '');
}

function currentTenantId(): string {
  return useAuthStore.getState().selectedTenant?.id ?? '';
}

/**
 * 4xx do canal é resposta, não falha passageira: 404 fora da área, 403 sem
 * permissão ou com a assinatura pendente. Só erro de servidor ou de rede
 * tenta de novo, uma vez.
 */
function whatsappRetry(failureCount: number, error: ApiError): boolean {
  const status = apiErrorStatus(error) ?? 0;
  return (status === 0 || status >= 500) && failureCount < 1;
}

export function useWhatsappStatus(enabled = true) {
  const tenantId = useTenantId();
  return useQuery<WhatsappStatus, ApiError>({
    queryKey: whatsappKeys.status(tenantId),
    queryFn: getWhatsappStatus,
    enabled: enabled && !!tenantId,
    refetchInterval: WHATSAPP_STATUS_POLL_MS,
    retry: whatsappRetry,
  });
}

/** Cria a sessão de signup; `panel.whatsapp_onboarding_started` sai no 201. */
export function useCreateWhatsappSignupSession() {
  const { track } = useAnalytics();
  return useMutation<WhatsappSignupSession, ApiError, void>({
    mutationFn: () => createWhatsappSignupSession(),
    onSuccess: (session) => {
      track('panel.whatsapp_onboarding_started', { mode: session.mode });
    },
  });
}

export interface CompleteWhatsappSignupVariables {
  sessionId: string;
  request: CompleteWhatsappSignupRequest;
  /** O modo da sessão, que vai no evento. */
  mode: WhatsappSignupMode;
  /** A loja já tinha conectado antes (vai no `panel.whatsapp_connected`). */
  reconnect: boolean;
}

/**
 * Conclui a sessão. No 200, o status novo entra no cache e sai
 * `panel.whatsapp_connected`; na recusa, `panel.whatsapp_onboarding_failed`
 * só para os códigos da tabela `COMPLETE_FAILURE_REASONS`.
 */
export function useCompleteWhatsappSignupSession() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<WhatsappStatus, ApiError, CompleteWhatsappSignupVariables>({
    mutationFn: ({ sessionId, request }) => completeWhatsappSignupSession(sessionId, request),
    onSuccess: (status, { mode, reconnect }) => {
      const tenantId = currentTenantId();
      if (tenantId) queryClient.setQueryData(whatsappKeys.status(tenantId), status);
      track('panel.whatsapp_connected', { mode, reconnect });
    },
    onError: (error) => {
      const reason = completeFailureReason(error);
      if (reason) track('panel.whatsapp_onboarding_failed', { reason });
      invalidateWhatsappStatus(queryClient, currentTenantId());
    },
  });
}

export interface AbandonWhatsappSignupVariables {
  sessionId: string;
  reason: WhatsappAbandonReason;
}

/** Registra o abandono; `panel.whatsapp_onboarding_failed` sai no 204, com o motivo informado. */
export function useAbandonWhatsappSignupSession() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<void, ApiError, AbandonWhatsappSignupVariables>({
    mutationFn: ({ sessionId, reason }) => abandonWhatsappSignupSession(sessionId, reason),
    onSuccess: (_, { reason }) => {
      track('panel.whatsapp_onboarding_failed', { reason });
    },
    onSettled: () => invalidateWhatsappStatus(queryClient, currentTenantId()),
  });
}

/** Desconecta pelo painel; `panel.whatsapp_disconnected` sai no 204. A feita pela Meta é medida no backend. */
export function useDisconnectWhatsapp() {
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  return useMutation<void, ApiError, void>({
    mutationFn: () => disconnectWhatsapp(),
    onSuccess: () => {
      track('panel.whatsapp_disconnected');
    },
    onSettled: () => invalidateWhatsappStatus(queryClient, currentTenantId()),
  });
}
